import { clsx } from 'clsx';
import { Children, ComponentProps, PropsWithChildren } from 'react';
import { isContentId } from 'src/contexts/ContentContext';
import { Tooltip } from 'src/core/ui';

import * as styles from './DivWithTitle.css';

/**
 * Gets text value - only works for simple text elements
 * @param element
 * @returns
 */
const getTextValueFromSingleChild = (
  element: React.ReactNode
): string | undefined => {
  if (element == null) {
    return undefined;
  }

  if (typeof element === 'string' || typeof element === 'number') {
    return element.toString();
  }

  const childrenArray = Children.toArray(element);
  const isSingleElement = childrenArray.length === 1;
  if (isSingleElement) {
    const childNode = (childrenArray[0] as React.ReactElement)?.props
      ?.children as React.ReactNode;

    if (childNode) {
      return getTextValueFromSingleChild(childNode);
    }
  }

  return undefined;
};

/**
 * Gets content Id recursively from single child
 * @param element
 * @returns
 */
const getContentIdFromSingleChild = (
  element: React.ReactNode
): string | undefined => {
  if (element == null) {
    return undefined;
  }

  if (typeof element === 'string' && isContentId(element)) {
    return element;
  }

  const propsId = (element as React.ReactElement).props?.id;
  if (propsId != null && isContentId(propsId)) {
    return propsId;
  }

  const childrenArray = Children.toArray(element);
  const isSingleElement = childrenArray.length === 1;
  if (isSingleElement) {
    const childNode = (childrenArray[0] as React.ReactElement)?.props
      ?.children as React.ReactNode;

    if (childNode) {
      return getContentIdFromSingleChild(childNode);
    }
  }

  return undefined;
};

export const DivWithTitle = ({
  children,
  showTooltip = true,
  showTooltipArrow = true,
  tooltipVariant,
  align = 'right',
  title,
  customTooltipContent,
  ...props
}: PropsWithChildren<ComponentProps<'div'>> & {
  showTooltip?: boolean;
  showTooltipArrow?: boolean;
  align?: 'center' | 'left' | 'right' | undefined;
  tooltipVariant?: 'theme' | 'light' | 'dark' | 'darkGray';
  customTooltipContent?: React.ReactNode;
}) => {
  const content = (
    <div {...props} className={clsx(props.className, styles.contentTitle)}>
      {children}
    </div>
  );

  const contentUnwrapped = (
    <div
      {...props}
      style={{
        ...props.style,
        whiteSpace: 'nowrap',
        overflow: 'unset',
        textOverflow: 'unset',
        minWidth: 'fit-content',
      }}
    >
      {children}
    </div>
  );

  if (showTooltip) {
    const mappedAligned =
      align === 'left' ? 'start' : align === 'right' ? 'end' : 'center';
    return (
      <Tooltip
        align={mappedAligned}
        triggerContent={content}
        contentVariant={tooltipVariant}
        showArrow={showTooltipArrow}
      >
        {customTooltipContent || title || contentUnwrapped}
      </Tooltip>
    );
  }

  return <>{content}</>;
};
